import cn from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CustomStylesCtx, useTextStyle } from '@webapp/common/components/custom-styles';
import { Slider } from '@webapp/common/components/slider';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { setHtml } from '@webapp/common/lib/ui';
import { applyStylesToClassNameWithImportant, striptags } from '@webapp/common/lib/utils';
import { AnswerType } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './scale-distribute.css';

export const ScaleDistribute: VFC<SurveyQuestion> = ({
    question: {
        id: questionId,
        answersActors: answersActorsRaw,
        params: { distributeLabel, distributeScore: disScore, distributeSurplus, distributeUnit },
    },
    questionBlockPosition,
}) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const [, updateRerenderHack] = useState(1);
    const questions = useStateSelector((s) => s.questions);
    useEffect(() => {
        const sub = () => updateRerenderHack((prev) => (prev + 1) % 1000);
        const unsubs = answersActorsRaw.map((actor) => {
            return actor.subscribe(sub);
        });
        return () => {
            unsubs.forEach((unsub) => unsub.unsubscribe());
        };
    }, []);
    const { answerStyle, listStyle } = useTextStyle();
    const distributeScore = parseInt(disScore.toString());
    const answersActors = answersActorsRaw.filter(
        (aa) => aa.getSnapshot().context.hidden !== true && aa.getSnapshot().context.hiddenByAnswersHide !== true,
    );
    const total = answersActors.reduce(
        (value, answerActor) =>
            value + ((answerActor.getSnapshot().context.response.getSnapshot().context.value as number) || 0),
        0,
    );
    const budget = Math.max(distributeScore - total, 0);
    const { text: textStyle } = useContext(CustomStylesCtx);
    const maxLen = useMemo(
        () =>
            answersActors.reduce(
                (max, answerActor) => Math.max(max, striptags(answerActor.getSnapshot().context.value[0]).length),
                0,
            ),
        [answersActors],
    );

    const labelStyle: CSSProperties = useMemo(
        () => ({
            ...textStyle,
            maxWidth: maxLen < 10 ? `calc(1ch * ${maxLen})` : undefined,
        }),
        [maxLen, textStyle],
    );
    const customStyles = useContext(CustomStylesCtx);
    const stylesCtx = useMemo(
        () => ({
            ...customStyles,
            textField: {
                ...(customStyles?.textField || {}),
                minWidth: 'unset',
            },
        }),
        [customStyles],
    );

    const style: CSSProperties = useMemo(
        () => ({
            ['--scale-label-width' as any]: maxLen < 10 ? `calc(1ch * ${maxLen})` : undefined,
            ...listStyle,
        }),
        [listStyle, maxLen],
    );

    useEffect(() => {
        applyStylesToClassNameWithImportant('.customized-track', {
            backgroundColor: answerStyle.color,
        });
    }, [answerStyle.color]);

    // Автодобивка остатка (#1149): после изменения варианта, если банк
    // распределён не полностью и незаполненным остался ровно один вариант —
    // ставим остаток в него. Если пустых вариантов больше одного, не трогаем:
    // непонятно, в какой из них добивать. Работает при включённой настройке
    // «Распределить без остатка» — только в этом случае сумму обязательно
    // доводить до distributeScore.
    const changeAnswerValue = useCallback(
        (answerActor: QuestionAnswerActor, value: number): void => {
            answerActor.getSnapshot().context.response.send({ type: 'CHANGE', value, questionId });

            if (!distributeSurplus) {
                return;
            }

            const sumOfOthers = answersActors.reduce((acc, item) => {
                if (item === answerActor) {
                    return acc;
                }

                return acc + (Number(item.getSnapshot().context.response.getSnapshot().context.value) || 0);
            }, 0);
            const rest = distributeScore - sumOfOthers - value;

            if (rest <= 0) {
                return;
            }

            const emptyAnswers = answersActors.filter(
                (item) => item !== answerActor && !item.getSnapshot().context.response.getSnapshot().context.value,
            );

            if (emptyAnswers.length !== 1) {
                return;
            }

            emptyAnswers[0].getSnapshot().context.response.send({ type: 'CHANGE', value: rest, questionId });
        },
        [answersActors, distributeScore, distributeSurplus, questionId],
    );

    const renderAnswers = useCallback(() => {
        return answersActors.map((answerActor) => {
            const {
                id,
                response,
                value: [value1],
                forwardParent,
            } = answerActor.getSnapshot().context;
            let renderedLabel = value1;
            const responseValue = Number(response.getSnapshot().context.value);
            const limit = Math.max(budget + responseValue, 0);
            let forwardParentAnswer: QuestionAnswerActor | null = null;
            questions.forEach((q) => {
                const founded = q.getSnapshot().context.answersActors.find((a) => {
                    return a.getSnapshot().context.id === forwardParent;
                });
                if (founded) {
                    forwardParentAnswer = founded;
                    if (forwardParentAnswer && forwardParentAnswer.getSnapshot().context.type === AnswerType.SELF) {
                        const sub = () => updateRerenderHack((prev) => (prev + 1) % 1000);
                        forwardParentAnswer.subscribe(sub);
                        renderedLabel = forwardParentAnswer
                            .getSnapshot()
                            .context.response.getSnapshot()
                            .context.extra?.toString();
                    }
                }
            });
            return (
                <div className={cn(CssPageBlock.ANSWER, css.answer)} key={id}>
                    <div
                        className={cn(CssUiComponent.LABEL, css.label)}
                        style={labelStyle}
                        {...setHtml(pipingAnswersReplacer(renderedLabel))}
                    />
                    <CustomStylesCtx.Provider value={stylesCtx}>
                        <Slider
                            className={cn(CssUiComponent.SLIDER, css.slider)}
                            controlClassName={css.control}
                            defaultValue={responseValue}
                            fieldClassName={css.field}
                            limit={limit}
                            max={distributeScore}
                            trackClassName='customized-track'
                            onChange={(value) => changeAnswerValue(answerActor, value)}
                        />
                    </CustomStylesCtx.Provider>
                </div>
            );
        });
    }, [
        answersActors,
        budget,
        changeAnswerValue,
        distributeScore,
        labelStyle,
        stylesCtx,
        questions,
        updateRerenderHack,
    ]);

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.SCALE_DISTRIBUTE, css.distribute)} style={style}>
            <div className={css.text} style={{ ...textStyle, justifyContent: questionBlockPosition }}>
                <span className={CssUiComponent.LABEL}>{distributeLabel}</span>
                <span className={cn(CssUiComponent.LABEL, css.score)} style={answerStyle}>
                    {budget}
                </span>
                <span className={CssUiComponent.LABEL}>{distributeUnit}</span>
            </div>
            <div className={css.answers} style={{ ...listStyle, justifyContent: questionBlockPosition }}>
                {renderAnswers()}
            </div>
        </div>
    );
};
ScaleDistribute.displayName = 'ScaleDistribute';
